<script setup>
/* eslint-disable no-undef */
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useDebounce } from '@vueuse/core';
import SearchAPI from 'dashboard/api/search';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: Number, required: true },
  messageIds: { type: Array, required: true },
});

const emit = defineEmits(['cancel', 'success']);
const { t } = useI18n();
const store = useStore();

const searchQuery = ref('');
const debouncedQuery = useDebounce(searchQuery, 300);
const conversations = ref([]);
const isSearching = ref(false);
const isForwarding = ref(false);
const selectedConversationId = ref(null);

const currentAccountId = computed(() => store.getters.getCurrentAccountId);

const apiUrl = computed(
  () => `/api/v1/accounts/${currentAccountId.value}/conversations`
);

async function searchConversations() {
  if (!debouncedQuery.value || debouncedQuery.value.length < 2) {
    conversations.value = [];
    return;
  }
  isSearching.value = true;
  try {
    const { data } = await SearchAPI.conversations({
      q: debouncedQuery.value,
    });
    conversations.value = (data.payload?.conversations || []).filter(
      c => c.id !== props.conversationId
    );
  } catch {
    conversations.value = [];
  } finally {
    isSearching.value = false;
  }
}

watch(debouncedQuery, searchConversations);

function selectConversation(id) {
  selectedConversationId.value = id;
}

async function forwardMessages() {
  if (!selectedConversationId.value) return;
  isForwarding.value = true;
  try {
    await axios.post(`${apiUrl.value}/${props.conversationId}/forwards`, {
      target_conversation_id: selectedConversationId.value,
      message_ids: props.messageIds,
    });
    useAlert(t('CONVERSATION.CONTEXT_MENU.FORWARD_SUCCESS'));
    emit('success');
    emit('cancel');
  } catch {
    useAlert(t('CONVERSATION.CONTEXT_MENU.FORWARD_ERROR'));
  } finally {
    isForwarding.value = false;
  }
}

function contactName(conversation) {
  return (
    conversation.contact?.name ||
    conversation.inbox?.name ||
    `#${conversation.id}`
  );
}

function contactThumbnail(conversation) {
  return conversation.contact?.thumbnail || '';
}

function lastMessage(conversation) {
  return conversation.message?.content || '';
}
</script>

<template>
  <woot-modal :show="show" :on-close="() => emit('cancel')">
    <div class="flex flex-col h-[500px] w-[420px]">
      <div class="flex items-center gap-3 p-4 border-b border-n-weak">
        <Icon icon="i-lucide-forward" class="size-5 text-n-slate-11" />
        <h3 class="text-base font-semibold text-n-slate-12 m-0">
          {{ t('CONVERSATION.CONTEXT_MENU.FORWARD_TITLE') }}
        </h3>
      </div>
      <div class="px-4 pt-3 pb-2">
        <input
          v-model="searchQuery"
          type="text"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          :placeholder="
            t('CONVERSATION.CONTEXT_MENU.FORWARD_SEARCH_PLACEHOLDER')
          "
        />
      </div>
      <div class="flex-1 overflow-y-auto px-2">
        <div v-if="isSearching" class="flex items-center justify-center py-8">
          <Spinner />
        </div>
        <div
          v-else-if="conversations.length === 0 && debouncedQuery.length >= 2"
          class="flex items-center justify-center py-8 text-sm text-n-slate-10"
        >
          {{ t('CONVERSATION.SEARCH.NO_RESULTS') }}
        </div>
        <div
          v-else-if="debouncedQuery.length < 2"
          class="flex items-center justify-center py-8 text-sm text-n-slate-10"
        >
          {{ t('CONVERSATION.CONTEXT_MENU.FORWARD_SEARCH_PLACEHOLDER') }}
        </div>
        <button
          v-for="conversation in conversations"
          :key="conversation.id"
          class="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg cursor-pointer transition-colors"
          :class="
            selectedConversationId === conversation.id
              ? 'bg-n-blue-3 dark:bg-n-blue-4'
              : 'hover:bg-n-alpha-2'
          "
          @click="selectConversation(conversation.id)"
        >
          <Avatar
            :name="contactName(conversation)"
            :src="contactThumbnail(conversation)"
            :size="36"
          />
          <div class="flex-1 min-w-0 text-left">
            <p class="text-sm font-medium text-n-slate-12 m-0 truncate">
              {{ contactName(conversation) }}
            </p>
            <p class="text-xs text-n-slate-10 m-0 truncate">
              #{{ conversation.id }}
              <span v-if="lastMessage(conversation)">
                — {{ lastMessage(conversation) }}
              </span>
            </p>
          </div>
          <Icon
            v-if="selectedConversationId === conversation.id"
            icon="i-lucide-check-circle"
            class="size-5 text-n-blue-9 flex-shrink-0"
          />
        </button>
      </div>
      <div
        class="flex items-center justify-end gap-2 p-4 border-t border-n-weak"
      >
        <button
          class="px-4 py-2 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
          @click="emit('cancel')"
        >
          {{ t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL') }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!selectedConversationId || isForwarding"
          @click="forwardMessages"
        >
          {{ t('CONVERSATION.CONTEXT_MENU.FORWARD') }}
        </button>
      </div>
    </div>
  </woot-modal>
</template>
